<template>
  <div class="home-page">
    <Hero />

    <section class="hidden">
      <h1>{{ t('home.seo.title') }}</h1>
      <p>{{ t('home.seo.p1') }}</p>
      <h2>{{ t('home.seo.h2a') }}</h2>
      <p>{{ t('home.seo.p2') }}</p>
      <h2>{{ t('home.seo.h2b') }}</h2>
      <ul>
        <li>{{ t('home.seo.li1') }}</li>
        <li>{{ t('home.seo.li2') }}</li>
        <li>{{ t('home.seo.li3') }}</li>
      </ul>
      <p>{{ t('home.seo.p3') }}</p>
    </section>

    <section class="py-16 md:py-24 bg-white relative overflow-hidden">
      <div class="absolute inset-0 opacity-5">
        <div class="absolute inset-0" style="background-image: radial-gradient(circle at 2px 2px, #6366f1 1px, transparent 0); background-size: 40px 40px"></div>
      </div>

      <div class="max-w-7xl mx-auto px-4 relative z-10">
        <div class="grid md:grid-cols-2 gap-12 items-center">
          <div class="space-y-6">
            <div class="inline-block mb-2">
              <span class="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-semibold rounded-full shadow-lg">
                {{ t('home.aboutBadge') }}
              </span>
            </div>
            <h2 class="text-4xl md:text-5xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent leading-[1.2] pb-2">
              {{ t('home.aboutTitle') }}
            </h2>
            <p class="text-gray-600 text-lg leading-relaxed">{{ t('home.aboutDesc') }}</p>

            <div class="grid grid-cols-2 gap-6 pt-1">
              <div class="space-y-2">
                <div class="flex items-center gap-2 text-indigo-600">
                  <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                  </svg>
                  <span class="font-semibold">{{ t('home.feature1Title') }}</span>
                </div>
                <p class="text-gray-600 text-sm ml-8">{{ t('home.feature1Desc') }}</p>
              </div>

              <div class="space-y-2">
                <div class="flex items-center gap-2 text-indigo-600">
                  <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                  </svg>
                  <span class="font-semibold">{{ t('home.feature2Title') }}</span>
                </div>
                <p class="text-gray-600 text-sm ml-8">{{ t('home.feature2Desc') }}</p>
              </div>
            </div>

            <router-link to="/sections" class="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
              {{ t('home.aboutAction') }}
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
              </svg>
            </router-link>
          </div>

          <div class="relative">
            <div class="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl transform rotate-6"></div>
            <img :src="aboutimg" alt="Students" class="relative rounded-3xl shadow-2xl w-full h-[400px] object-cover" />
          </div>
        </div>
      </div>
    </section>

    <section class="py-16 md:py-24 bg-gradient-to-br from-slate-50 to-slate-100">
      <div class="max-w-7xl mx-auto px-4">
        <div class="text-center mb-12">
          <div class="inline-block mb-4">
            <span class="px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-sm font-bold rounded-full shadow-lg">
              {{ t('news.badge') }}
            </span>
          </div>
          <h2 class="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent leading-[1.2] pb-2">
            {{ t('news.title') }}
          </h2>
        </div>

        <div v-if="newsLoading" class="text-center py-16">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
          <p class="mt-4 text-gray-600">{{ t('common.loading') }}</p>
        </div>

        <div v-else class="grid gap-8 md:grid-cols-3">
          <div v-for="(news, i) in latestNews" :key="news._id || i" class="group bg-white/60 backdrop-blur-xl rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-white/50">
            <div class="relative overflow-hidden">
              <img :src="news.img || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400'" :alt="news.title" class="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-110" />
              <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div class="absolute top-4 right-4">
                <span class="px-3 py-1.5 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-semibold rounded-full shadow-lg">
                  {{ formatDate(news.publishedAt) }}
                </span>
              </div>
            </div>
            <div class="p-6">
              <h3 class="text-2xl font-bold mb-3 text-gray-800 group-hover:text-indigo-600 transition-colors line-clamp-2">{{ news.title }}</h3>
              <p class="text-gray-600 mb-4 line-clamp-3">{{ news.description }}</p>
              <router-link to="/news" class="text-indigo-600 font-semibold flex items-center group-hover:translate-x-2 transition-transform">
                {{ t('news.readMore') }}
                <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                </svg>
              </router-link>
            </div>
          </div>
        </div>

        <div class="text-center mt-12">
          <router-link to="/news" class="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-2xl font-bold shadow-xl hover:shadow-2xl hover:scale-110 transition-all">
            {{ t('home.newsAll') }}
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
            </svg>
          </router-link>
        </div>
      </div>
    </section>

    <TeachersPreview />

    <section class="py-16 md:py-24 bg-white">
      <div class="max-w-7xl mx-auto px-4">
        <div class="text-center mb-12">
          <div class="inline-block mb-4">
            <span class="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-bold rounded-full shadow-lg">
              {{ t('sections.badge') }}
            </span>
          </div>
          <h2 class="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent leading-[1.2] pb-2">
            {{ t('sections.title') }}
          </h2>
        </div>

        <div v-if="sectionsLoading" class="text-center py-12">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
          <p class="mt-4 text-gray-600">{{ t('home.sectionsLoading') }}</p>
        </div>

        <div v-else-if="sectionsPreview.length" class="grid md:grid-cols-3 gap-8">
          <div v-for="(section, i) in sectionsPreview" :key="section._id || i" class="bg-white/60 backdrop-blur-xl rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-white/50 cursor-pointer">
            <h3 class="text-2xl font-bold mb-3 text-gray-900">{{ section.name }}</h3>
            <p class="text-gray-600 mb-4">{{ section.groups.length ? section.groups.slice(0, 2).join(', ') : t('home.groupsEmpty') }}</p>
            <div class="text-sm text-indigo-600 font-semibold">{{ t('sections.groupCount', { count: section.groups.length }) }}</div>
          </div>
        </div>

        <div v-else class="text-center py-12 text-gray-600">{{ t('sections.empty') }}</div>

        <div class="text-center mt-12">
          <router-link to="/sections" class="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-2xl font-bold shadow-xl hover:shadow-2xl hover:scale-110 transition-all">
            {{ t('home.sectionsAll') }}
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
            </svg>
          </router-link>
        </div>
      </div>
    </section>

    <Map />

    <section class="py-16 md:py-24 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 relative overflow-hidden">
      <div class="absolute inset-0">
        <div class="absolute inset-0 bg-black/20"></div>
        <div v-for="i in 30" :key="i" class="absolute bg-white/5 rounded-full" :style="{ width: Math.random() * 200 + 50 + 'px', height: Math.random() * 200 + 50 + 'px', left: Math.random() * 100 + '%', top: Math.random() * 100 + '%', animationDelay: Math.random() * 5 + 's' }"></div>
      </div>

      <div class="max-w-4xl mx-auto px-4 text-center relative z-10">
        <h2 class="text-4xl md:text-5xl font-black text-white mb-6">{{ t('home.ctaTitle') }}</h2>
        <p class="text-xl text-white/90 mb-8 leading-relaxed">{{ t('home.ctaDesc') }}</p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <router-link to="/contact" class="px-8 py-4 bg-white text-indigo-600 rounded-2xl font-bold shadow-xl hover:shadow-2xl hover:scale-110 active:scale-95 transition-all duration-300">
            {{ t('home.ctaContact') }}
          </router-link>
          <router-link to="/sections" class="px-8 py-4 bg-white/20 backdrop-blur-xl text-white border-2 border-white/30 rounded-2xl font-bold hover:bg-white hover:text-indigo-600 transition-all duration-300 hover:scale-110 active:scale-95">
            {{ t('home.ctaSections') }}
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/services/api';
import aboutimg from '@/assets/yonalishlar1.jpg';
import Hero from '@/components/Hero.vue';
import TeachersPreview from '@/components/TeachersPreview.vue';
import Map from '@/components/Map.vue';

export default {
  name: 'Home',
  components: { Hero, TeachersPreview, Map },
  setup() {
    const { t, locale } = useI18n();
    const newsList = ref([]);
    const newsLoading = ref(true);
    const sectionsList = ref([]);
    const sectionsLoading = ref(true);

    const latestNews = computed(() => newsList.value.slice(0, 3));

    const formatDate = (date) => {
      if (!date) return t('common.unknown');
      const d = new Date(date);
      const monthsByLocale = {
        uz: ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun', 'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr'],
        ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
        en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      };
      const months = monthsByLocale[locale.value] || monthsByLocale.uz;
      return `${d.getDate()} ${months[d.getMonth()]}`;
    };

    const fetchLatestNews = async () => {
      newsLoading.value = true;
      try {
        const res = await api.get('/api/news');
        let newsData = [];
        if (Array.isArray(res.data)) newsData = res.data;
        else if (res.data.news && Array.isArray(res.data.news)) newsData = res.data.news;
        else if (res.data.data && Array.isArray(res.data.data)) newsData = res.data.data;

        newsData.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
        newsList.value = newsData;
      } catch (err) {
        console.error('News API error:', err);
        newsList.value = [];
      } finally {
        newsLoading.value = false;
      }
    };

    const fetchSectionsPreview = async () => {
      sectionsLoading.value = true;
      try {
        const res = await api.get('/api/sections');
        const sectionsData = Array.isArray(res.data) ? res.data : [];
        sectionsList.value = sectionsData.map((item) => ({
          ...item,
          groups: Array.isArray(item.groups) ? item.groups : [],
        }));
      } catch (err) {
        console.error('Sections API error:', err);
        sectionsList.value = [];
      } finally {
        sectionsLoading.value = false;
      }
    };

    const sectionsPreview = computed(() => sectionsList.value.slice(0, 3));

    onMounted(() => {
      fetchLatestNews();
      fetchSectionsPreview();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    return {
      t,
      aboutimg,
      newsLoading,
      latestNews,
      sectionsLoading,
      sectionsPreview,
      formatDate,
    };
  },
};
</script>

<style scoped>
.home-page {
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(10deg);
  }
}

.absolute.bg-white\/5 {
  animation: float linear infinite;
  animation-duration: 10s;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
